<template>
    <modal
      name="lead-flow-direct-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideCreateDirectModal" class="popup__close" type="button" :disabled="saving" aria-label="Close"></button>
        <div class="popup__title">
          <span>New Direct Lead</span>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormDirect">
            <p v-if="loading">Loading form options…</p>
            <p v-if="loadError" role="alert">{{ loadError }}</p>
            <app-grid>
              <template #item-1>
                <app-form-group label="Date of request" required>
                  <app-date-picker
                    v-model="direct_form.created_at"
                    format="DD MMM YYYY HH:mm"
                    value-type="YYYY-MM-DDTHH:mm"
                    type="datetime"
                    placeholder="Date of request"
                    :error="$v.direct_form.created_at.$error"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.created_at.$dirty && !$v.direct_form.created_at.required">Required field
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Source" required>
                  <app-select
                    v-model="direct_form.source"
                    :options="source_options"
                    :reduce="item => item.code"
                    :clearable="false"
                    placeholder="Source"
                    :error="$v.direct_form.source.$error"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.source.$dirty && !$v.direct_form.source.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="First name" label-for="lead_first_name" required>
                  <app-input
                    v-model="direct_form.first_name"
                    :error="$v.direct_form.first_name.$error"
                    id="lead_first_name"
                    placeholder="First name"
                    name="lead_first_name"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.first_name.$dirty && !$v.direct_form.first_name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Last name" label-for="lead_last_name">
                  <app-input
                    v-model="direct_form.last_name"
                    id="lead_last_name"
                    placeholder="Last name"
                    name="lead_last_name"
                  />
                </app-form-group>
                <app-form-group label="Language" required>
                  <app-select
                    v-model="direct_form.language"
                    :options="languages_options"
                    :reduce="item => item.code"
                    :filterable="false"
                    :clearable="false"
                    :searchable="false"
                    placeholder="Language"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.language.$dirty && !$v.direct_form.language.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Pax" label-for="lead_direct_pax">
                  <app-input
                    v-model.number="direct_form.pax"
                    type="number"
                    min="0"
                    id="lead_direct_pax"
                    placeholder="Pax"
                    name="lead_direct_pax"
                  />
                </app-form-group>
                <app-form-group label="Tour operator">
                  <app-select
                    v-model="direct_form.tour_operator"
                    :options="tour_operators_options"
                    :reduce="item => item.id"
                    placeholder="Tour operator"
                  />
                </app-form-group>
                <app-form-group label="Country">
                  <app-select
                    v-model="direct_form.country"
                    @input="onCountryChange"
                    :options="country_options"
                    :reduce="item => item.code"
                    placeholder="Country"
                  />
                </app-form-group>
                <app-form-group label="Time zone" :disabled="!direct_form.country">
                  <app-select
                    v-model="direct_form.country_timezone"
                    :reduce="item => item.id"
                    :options="direct_timezone_options"
                    :disabled="!direct_form.country"
                    :display-field="timezoneLabel"
                    label="label"
                    placeholder="Time zone"
                  />
                </app-form-group>
                <app-form-group label="Travel time">
                  <app-date-picker
                    v-model="direct_form.travel_time"
                    format="MMM YYYY"
                    placeholder="Travel time"
                    type="month"
                  />
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Activities" class="form-group--mh">
                  <app-cells :indent="false">
                    <app-checkbox-pic v-model="direct_form.climbing" activeColor="#219b52"
                                      picPath="climbing-table-icon.svg"/>
                    <app-checkbox-pic v-model="direct_form.safari" activeColor="#eb9514"
                                      picPath="safari-table-icon.svg"/>
                    <app-checkbox-pic v-model="direct_form.day_trip" activeColor="#77b017"
                                      picPath="day_trips-table-icon.svg"/>
                    <app-checkbox-pic v-model="direct_form.island" activeColor="#da3e63"
                                      picPath="island-table-icon.svg"/>
                  </app-cells>
                </app-form-group>
                <app-form-group label="Preferred communication method" required>
                  <app-select
                    v-model="direct_form.preferred_communication_method"
                    :options="[{ code: 'whatsapp', name: 'WhatsApp' }, { code: 'email', name: 'Email' }]"
                    :reduce="item => item.code"
                    :clearable="false"
                    :searchable="false"
                    :error="$v.direct_form.preferred_communication_method.$error"
                    placeholder="Select communication method"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.preferred_communication_method.$error">Select WhatsApp or Email</div>
                  </template>
                </app-form-group>
                <app-form-group label="E-mail" label-for="direct-email" :required="direct_form.preferred_communication_method === 'email'">
                  <app-input
                    v-model="direct_form.email"
                    @change.native="$v.direct_form.email.$touch"
                    :error="$v.direct_form.email.$error"
                    id="direct-email"
                    type="email"
                    placeholder="E-mail"
                    name="direct-email"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.email.$dirty && !$v.direct_form.email.required">Email is required for this method</div>
                    <div v-if="$v.direct_form.email.$dirty && !$v.direct_form.email.email">Enter the correct e-mail
                      format
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Phone" :required="direct_form.preferred_communication_method === 'whatsapp'">
                  <app-phone
                    @change.native="$v.direct_form.phone.$touch()"
                    v-model="direct_form.phone"
                    placeholder="+255 786 350 350"
                    :error="$v.direct_form.phone.$error"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.phone.$dirty && !$v.direct_form.phone.required">Phone is required for WhatsApp</div>
                    <div v-if="$v.direct_form.phone.$dirty && direct_form.phone === 0">
                      Invalid phone number format
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Facebook" label-for="direct-facebook">
                  <app-input
                    v-model="direct_form.facebook"
                    :error="$v.direct_form.facebook.$error"
                    id="direct-facebook"
                    placeholder="Facebook"
                    name="direct-facebook"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.facebook.$invalid">Wrong link</div>
                  </template>
                </app-form-group>
                <app-form-group label="Instagram" label-for="direct-instagram">
                  <app-input
                    v-model="direct_form.instagram"
                    :error="$v.direct_form.instagram.$error"
                    id="direct-instagram"
                    placeholder="Instagram"
                    name="direct-instagram"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.instagram.$invalid">Wrong link</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-form-group label="Request" label-for="direct_notes" required>
              <app-textarea
                v-model="direct_form.direct_request_info"
                id="direct_notes"
                placeholder="Request"
                :error="$v.direct_form.direct_request_info.$error"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" :disabled="saving || loading || Boolean(loadError)">{{ saving ? 'Creating…' : 'Create' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
</template>

<script>
import { email, required, requiredIf, url } from 'vuelidate/lib/validators'
import AppCheckboxPic from '@/components/AppCheckboxPic'
import { getClientsTourOperators, getCountries, getCountryTimezones, getLanguages, getLeadsSources, postDirectLead } from '@/http'

export default {
  name: 'LeadDirectCreateModal',
  components: { AppCheckboxPic },
  data() {
    return {
      direct_form: {},
      source_options: [],
      languages_options: [],
      country_options: [],
      tour_operators_options: [],
      direct_timezone_options: [],
      loading: false,
      loadError: '',
      saving: false
    }
  },
  validations: {
    direct_form: {
      first_name: { required },
      source: { required },
      language: { required },
      created_at: { required },
      direct_request_info: { required },
      preferred_communication_method: { required, supported: value => !value || ['whatsapp', 'email'].includes(value) },
      email: {
        email,
        required: requiredIf(function () { return this.direct_form.preferred_communication_method === 'email' })
      },
      phone: {
        valid: value => value !== 0,
        required: requiredIf(function () { return this.direct_form.preferred_communication_method === 'whatsapp' })
      },
      facebook: { url },
      instagram: { url }
    }
  },
  methods: {
    async open() {
      this.direct_form = { language: 'us', country: null, country_timezone: null }
      this.direct_timezone_options = []
      this.$v.$reset()
      this.$modal.show('lead-flow-direct-modal')
      this.loading = true
      this.loadError = ''
      try {
        const [sources, languages, countries, operators] = await Promise.all([getLeadsSources(), getLanguages(), getCountries(), getClientsTourOperators()])
        this.source_options = sources.data
        this.languages_options = languages.data
        this.country_options = countries.data
        this.tour_operators_options = operators.data
        if (!this.languages_options.some(item => item.code === this.direct_form.language)) {
          this.direct_form.language = null
        }
      } catch (_) {
        this.loadError = 'Unable to load form options. Close the form and try again.'
      } finally {
        this.loading = false
      }
    },
    onHideCreateDirectModal() {
      if (!this.saving) this.$modal.hide('lead-flow-direct-modal')
    },
    timezoneLabel(timezone) {
      return timezone.label || timezone.timezone_name
    },
    async onCountryChange() {
      this.direct_form.country_timezone = null
      this.direct_timezone_options = []
      const country = this.direct_form.country
      if (!country) return
      try {
        const { data } = await getCountryTimezones(country)
        if (this.direct_form.country === country) this.direct_timezone_options = data
      } catch (_) {
        this.$notify({ type: 'error', title: 'Unable to load time zones' })
      }
    },
    async onCheckFormDirect() {
      if (this.saving || this.loading || this.loadError) return
      this.direct_form.first_name = (this.direct_form.first_name || '').trim()
      this.direct_form.last_name = (this.direct_form.last_name || '').trim()
      this.$v.$touch()
      if (this.$v.$invalid || this.direct_form.phone === 0) {
        this.$notify({ type: 'info', title: 'Check that the form fields are filled in correctly' })
        return
      }
      if (!['climbing', 'safari', 'day_trip', 'island'].some(key => this.direct_form[key])) {
        this.$notify({ type: 'info', title: 'Select at least one activity' })
        return
      }
      if (!['email', 'phone', 'facebook', 'instagram'].some(key => this.direct_form[key])) {
        this.$notify({ type: 'info', title: 'Provide at least one communication method' })
        return
      }
      const payload = {
        name: [this.direct_form.first_name, this.direct_form.last_name]
          .filter(Boolean).map(value => value.trim()).filter(Boolean).join(' ')
      }
      Object.keys(this.direct_form).forEach(key => {
        const value = this.direct_form[key]
        if (value !== null && value !== undefined && value !== '') payload[key] = value
      })
      this.saving = true
      try {
        await postDirectLead({ ...payload, flow_version: 'v2' })
        this.$modal.hide('lead-flow-direct-modal')
        this.$notify({ type: 'success', title: 'Direct lead created. Available in Task Tracker → New Leads.' })
        this.$emit('created')
      } catch (_) {
        // The shared API interceptor displays the server validation message.
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
